import * as React from 'react';
import '@ant-design/compatible/assets/index.css';
import {
	Modal,
	Input,
	Alert,
	Select,
	Switch,
	Button,
	Form,
	Checkbox,
	Spin,
	Row,
	Radio,
	Popover,
} from 'antd';
import { connect } from 'react-redux';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { DownCircleOutlined, InfoCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import BuyerRegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import { BUYER_ADMIN_ONLY } from '../roles/roles';
import EmailComponentAntdV4 from '../email_component/EmailComponentAntdV4';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { fetchUserForBuyer } from '../../thunks/users_thunks';
import { BUYER_CONTACTS_CRUD_ACTION_CREATORS } from '../../actions/buyer_contacts_actions';
import OWForm from '../ow_form/OWForm';
import BrandSelection from '../brand_selection/BrandSelection';

const FormItem = Form.Item;

function BuyerUserInviteForm(props) {
	const {
		visible,
		onCancel,
		onSubmit,
		formData,
		createErrors,
		updateErrors,
		locations,
		isLocationChange,
		fetchUser,
		isReinvite,
	} = props;
	const [form] = Form.useForm();

	const { setFieldsValue } = form;

	const [showPassword, setShowPassword] = React.useState(false);
	const isSharedContact = Form.useWatch('isSharedContact', form);

	const [loading, setLoading] = useState(false);
	const [hasBrands, setHasBrands] = useState(false);

	const email = useMemo(
		() => (visible ? nullSafeGet('contact.email', formData) : null),
		[formData, visible]
	);

	const roles = Form.useWatch('roles', form);
	const includesBuyerAdmin = BUYER_ADMIN_ONLY.has(roles);

	const hasAccessToAllLocations = Form.useWatch('hasAccessToAllLocations', form);
	const byLocationOrBrand = Form.useWatch('byLocationOrBrand', form);

	const toggleAccessType = useMemo(
		() => (value) => {
			form.setFieldsValue({
				locationIds: [],
				brandIds: [],
				byLocationOrBrand: value,
			});
		},
		[]
	);

	const refreshUser = useCallback(() => {
		setLoading(true);
		fetchUser(email)
			.then((user) => {
				const locationOrBrand =
					nullSafeGetOrElse('brandIds', user, []).length > 0 ? 'brand' : 'location';
				setFieldsValue({
					email: nullSafeGet('contact.email', user),
					nameGiven: nullSafeGet('contact.nameGiven', user),
					nameFamily: nullSafeGet('contact.nameFamily', user),
					roles:
						nullSafeGetOrElse('role', user, []).length > 0
							? nullSafeGet('role', user)[0]
							: 'BUYER_STORE_ASSOCIATE',
					locationIds: nullSafeGetOrElse('locationIds', user, []),
					hasAccessToAllLocations: !!user.hasAccessToAllLocations,
					isSharedContact: nullSafeGetOrElse('contact.isSharedContact', user, false),
					readOnlyAccess: nullSafeGetOrElse('contact.readOnlyAccess', user, false),
					brandIds: nullSafeGetOrElse('brandIds', user, []),
					byLocationOrBrand: locationOrBrand,
				});
			})
			.finally(() => setLoading(false));
	}, [email, fetchUser, setFieldsValue]);

	useEffect(() => {
		email && refreshUser();
	}, [email, fetchUser, refreshUser]);

	const handleSubmit = React.useCallback(
		(values) => {
			const hasAccessToAllLocations = nullSafeGetOrElse('hasAccessToAllLocations', values, false);
			const brandIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'location'), 'location')
					? []
					: values.brandIds;
			const locationIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'brand'), 'brand')
					? []
					: values.locationIds;
			const updatedValues = {
				...values,
				hasAccessToAllLocations: hasAccessToAllLocations,
				brandIds: brandIds,
				locationIds: locationIds,
			};

			onSubmit && onSubmit(updatedValues);
		},
		[onSubmit]
	);
	return (
		<Modal
			visible={visible}
			width={600}
			title={'Invite New User'}
			okText={'Submit'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={true}
			destroyOnClose={true}
		>
			{loading ? (
				<Spin />
			) : (
				<OWForm
					form={form}
					layout="vertical"
					className="supplierForm"
					preserve={false}
					initialValues={{
						passwordResetRequired: false,
						isSharedContact: false,
						email: '',
						roles: 'BUYER_STORE_ASSOCIATE',
						hasAccessToAllLocations: true,
						byLocationOrBrand: 'location',
					}}
					onFinish={handleSubmit}
					actionCreators={BUYER_CONTACTS_CRUD_ACTION_CREATORS}
				>
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					<EmailComponentAntdV4 defaultValue={email} disabled={isReinvite} />
					<FormItem
						label={'Name'}
						className={'flex flex-row'}
						required={true}
						rules={[
							{
								required: true,
								message: 'Please input name.',
							},
						]}
					>
						<div className={'flex flex-row'} style={{ marginBottom: -24 }}>
							<FormItem name={'nameGiven'}>
								<Input
									placeholder="Given name"
									style={{ maxWidth: 200 }}
									disabled={false}
									defaultValue={nullSafeGet('contact.nameGiven', formData)}
								/>
							</FormItem>
							<FormItem name={'nameFamily'}>
								<Input
									defaultValue={nullSafeGet('contact.nameFamily', formData)}
									placeholder="Family name"
									style={{ maxWidth: 200, marginLeft: 8 }}
									disabled={false}
								/>
							</FormItem>
						</div>
					</FormItem>

					<FormItem
						label="Roles"
						name="roles"
						required={true}
						rules={[
							{
								required: true,
								message: 'Please select a role',
							},
						]}
					>
						<Select
							disabled={isLocationChange ? isLocationChange : false}
							style={{ maxWidth: 200 }}
							onChange={(role) => {
								form.setFieldsValue({
									hasAccessToAllLocations: BUYER_ADMIN_ONLY.has(role),
								});
							}}
						>
							<Select.Option value="BUYER_ADMIN">Admin</Select.Option>
							<Select.Option value="BUYER_USER">Manager</Select.Option>
							<Select.Option value="BUYER_STORE_ASSOCIATE">Store Associate</Select.Option>
						</Select>
					</FormItem>
					<FormItem
						name="hasAccessToAllLocations"
						valuePropName="checked"
						required={true}
						hidden={includesBuyerAdmin}
					>
						<Row>
							<Switch
								checked={!!form.getFieldValue('hasAccessToAllLocations')}
								onChange={(checked) => form.setFieldsValue({ hasAccessToAllLocations: !!checked })}
							/>
							<label style={{ marginLeft: 8 }}>Select All Locations</label>
						</Row>
					</FormItem>
					<FormItem
						name="byLocationOrBrand"
						required={!hasAccessToAllLocations && hasBrands}
						hidden={hasAccessToAllLocations || !hasBrands}
						label={'Restrict Access By'}
					>
						<Radio.Group
							onChange={(e) => {
								toggleAccessType(e.target.value);
							}}
							value={form.getFieldValue('byLocationOrBrand')}
						>
							<Radio.Button value="location">Location</Radio.Button>
							<Radio.Button value="brand">Brand</Radio.Button>
						</Radio.Group>
					</FormItem>
					<FormItem
						name="brandIds"
						hidden={
							includesBuyerAdmin ||
							hasAccessToAllLocations ||
							!compareStrings(byLocationOrBrand, 'brand')
						}
						required={true}
						label={
							<Row>
								<span>Brands</span>
								<Popover
									placement={'topLeft'}
									content={
										'Selecting a brand will give the user access to all locations in that brand.'
									}
								>
									<InfoCircleOutlined
										style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}
									/>
								</Popover>
							</Row>
						}
						rules={[
							{
								type: 'array',
								required: !hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'brand'),
								message: 'Please select a brand',
							},
						]}
					>
						<BrandSelection setHasBrands={setHasBrands} />
					</FormItem>
					<FormItem
						name="locationIds"
						hidden={
							includesBuyerAdmin ||
							hasAccessToAllLocations ||
							!compareStrings(byLocationOrBrand, 'location')
						}
						required={true}
						label={'Locations'}
						rules={[
							{
								type: 'array',
								required: !hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'location'),
								message: 'Please select a location',
							},
						]}
					>
						<BuyerRegionsLocationsTreeSelect
							mode="multiple"
							noIsActiveFilter
							disabled={hasAccessToAllLocations}
							filtersSlice={locations}
							filtersValueAccessor={() => form.getFieldValue(FILTER_CONFIG_NAMES.LOCATION)}
							targetCollectionName={'buyerUsersLocations'}
						/>
					</FormItem>
					<div className="flex flex-row items-baseline">
						<FormItem name="isSharedContact" valuePropName="checked">
							<Switch />
						</FormItem>
						<label className="ml-2">Shared Account</label>
					</div>
					{!includesBuyerAdmin && (
						<div className="flex flex-row items-baseline">
							<FormItem name="readOnlyAccess" valuePropName="checked">
								<Switch />
							</FormItem>
							<label className="ml-2">Read Only Access</label>
						</div>
					)}
					<div style={{ marginTop: 12 }}>
						{showPassword || isSharedContact ? (
							<>
								{showPassword ? (
									<FormItem>
										<Button
											style={{ padding: 0 }}
											type={'link'}
											onClick={() => setShowPassword(false)}
										>
											<UpCircleOutlined translate="" />
											<span style={{ textTransform: 'capitalize' }}>Hide Password</span>
										</Button>
									</FormItem>
								) : null}

								<FormItem
									name="password"
									label={
										<span>
											Password <span style={{ color: 'rgba(0,0,0,0.45)' }}>(Optional)</span>
										</span>
									}
									required={false}
									style={{ marginBottom: 0 }}
								>
									<Input type="password" style={{ maxWidth: 200 }} />
								</FormItem>
								<FormItem name="passwordResetRequired" valuePropName="checked" required={false}>
									<Checkbox>Ask user to change password when they sign in</Checkbox>
								</FormItem>
							</>
						) : (
							<FormItem>
								<Button style={{ padding: 0 }} type={'link'} onClick={() => setShowPassword(true)}>
									<DownCircleOutlined translate="" />
									<span style={{ textTransform: 'capitalize' }}>Set password</span>
								</Button>
							</FormItem>
						)}
					</div>
				</OWForm>
			)}
		</Modal>
	);
}

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.buyer_contacts.creating,
	updating: state.buyer_contacts.updating,
	currentUser: state.session.currentUser,

	buyerUsers: state.buyer_users,
	locations: state.locations,
	regions: state.regions,
});

const mapDispatchToProps = (dispatch) => ({
	fetchUser: (email) => dispatch(fetchUserForBuyer(email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BuyerUserInviteForm);
